import { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import type { FileLocation } from "@egalregal/shared";
import { PhotoCameraIcon, MicIcon, AttachFileIcon } from "../../shared/components/icons";
import { CapturePanel } from "./CapturePanel";
import { CameraDialog, type CameraShot } from "./CameraDialog";
import { useT } from "../../shared/i18n";

export interface StagedCapture {
  file: File;
  kind: "photo" | "audio";
  location?: FileLocation;
}

/**
 * Shared capture surface (ticket 14): the Camera / Record / File CTA row plus
 * the photo camera dialog, the inline memo recorder and a hidden device-file
 * input. Everything captured funnels through onStaged so the consumer decides
 * what to do with it (stage for upload on save, upload straight to a
 * container, ...).
 *
 * @param photoOnly When true, hides the audio recorder and restricts the file
 *   picker to images — for item photo capture, which mirrors the container
 *   UI but never records memos.
 * @param usedNames names already staged in the current capture batch, so a
 *   same-second capture gets a deterministic `-2`, `-3`, … suffix.
 */
export function CaptureToolbar({
  onStaged,
  photoOnly = false,
  usedNames = [],
}: {
  onStaged: (capture: StagedCapture) => void;
  photoOnly?: boolean;
  usedNames?: readonly string[];
}) {
  const t = useT();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [memoOpen, setMemoOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const keepCaptured = (file: File, _kind: "audio") => {
    onStaged({ file, kind: "audio" });
  };

  const keepShots = (shots: CameraShot[]) => {
    for (const shot of shots) {
      onStaged({ file: shot.file, kind: "photo", location: shot.location ?? undefined });
    }
  };

  const addFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    for (const file of files) {
      const kind: "photo" | "audio" = photoOnly ? "photo" : file.type.startsWith("audio/") ? "audio" : "photo";
      onStaged({ file, kind });
    }
    e.target.value = "";
  };

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<PhotoCameraIcon />}
          onClick={() => setCameraOpen(true)}
        >
          {t("captureCamera")}
        </Button>
        {!photoOnly && (
          <Button
            variant="outlined"
            startIcon={<MicIcon />}
            onClick={() => setMemoOpen(true)}
          >
            {t("captureRecord")}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<AttachFileIcon />}
          onClick={() => fileInput.current?.click()}
        >
          {t("captureFile")}
        </Button>
      </Stack>

      {memoOpen && !photoOnly && (
        <CapturePanel onCapture={keepCaptured} onClose={() => setMemoOpen(false)} usedNames={usedNames} />
      )}

      {cameraOpen && (
        <CameraDialog open onClose={() => setCameraOpen(false)} onCapture={keepShots} usedNames={usedNames} />
      )}

      <input
        ref={fileInput}
        type="file"
        accept={photoOnly ? "image/*" : "image/*,audio/*"}
        hidden
        onChange={addFiles}
      />
    </>
  );
}